import React from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { FieldGroup } from '@xsolla/ui-core';

import { LoginProjectConfiguration } from '../../../api';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { VerticalHorizontalCenterBlock } from '../../../styled';
import {
  FieldBlock,
  FieldInput,
  FormFooter,
  HeaderTitle,
  Preloader,
  ToDashboardError
} from '../../ui';
import { TermsAndPoliciesField } from './types';
import { TermsAndPoliciesValidator } from './validator';

type Props = RouteComponentProps & {
  projectId: string;
};

const TermsAndPoliciesForm = ({ projectId, history }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();
  const {
    currentProjectFetching,
    currentProject,
    currentUpdateFetching,
    updateCurrentProject,
    error
  } = useCurrentProject(projectId);

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  const onSubmit = async (values: LoginProjectConfiguration) => {
    try {
      await updateCurrentProject(values);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    }
  };

  if (currentProjectFetching) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  if (error) {
    return <ToDashboardError pageName="terms-and-policies_open" error={error} />;
  }

  return (
    <Form onSubmit={onSubmit} initialValues={currentProject} validate={TermsAndPoliciesValidator}>
      {({ handleSubmit }) => {
        return (
          <>
            <HeaderTitle
              title={intl.formatMessage({ id: 'terms-and-policies.settings.title' })}
              subtitle={intl.formatMessage({ id: 'terms-and-policies.settings.subtitle' })}
            />

            <FieldBlock>
              <FieldGroup
                size="sm"
                label={intl.formatMessage({
                  id: 'terms-and-policies.tos-url.input.title'
                })}>
                <FieldInput name={TermsAndPoliciesField.TosUrl} />
              </FieldGroup>
              <FieldGroup
                size="sm"
                label={intl.formatMessage({
                  id: 'terms-and-policies.privacy-policy-url.input.title'
                })}>
                <FieldInput name={TermsAndPoliciesField.PrivacyPolicy} />
              </FieldGroup>
              <FieldGroup
                size="sm"
                label={intl.formatMessage({
                  id: 'terms-and-policies.cookies-policy-url.input.title'
                })}>
                <FieldInput name={TermsAndPoliciesField.CookiePolicy} />
              </FieldGroup>
            </FieldBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={currentUpdateFetching}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(TermsAndPoliciesForm);
